import type { ReactNode } from 'react';
import { usePermissions } from '@/hooks/usePermissions';

interface RequirePermissionProps {
  /** The permission key that must be held, e.g. 'tenant.admin.roles'. */
  permission: string;
  /** Content to render when the permission is satisfied. */
  children: ReactNode;
  /** Optional fallback when the permission is not held (defaults to null). */
  fallback?: ReactNode;
}

/**
 * Story 45.6 — declarative permission guard component.
 *
 * Renders `children` only if the current principal holds `permission`.
 * Renders `fallback` (default: nothing) otherwise.
 *
 * ```tsx
 * <RequirePermission permission="tenant.admin.roles">
 *   <Button onClick={handleCreateRole}>Create role</Button>
 * </RequirePermission>
 * ```
 *
 * This is a thin wrapper around `usePermissions().can(permission)`.
 * For route-level guarding, keep using `ProtectedRoute`; this component
 * is for intra-page UI element visibility.
 */
export function RequirePermission({
  permission,
  children,
  fallback = null,
}: RequirePermissionProps) {
  const { can } = usePermissions();
  return can(permission) ? <>{children}</> : <>{fallback}</>;
}
